// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import Multiselect from '../../Multiselect';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';
import { getErrorHelperText, isValueEmpty } from '../../../../utils/Utils';
import { useSingleAttributeQuery } from '../../../../features/configuration/attributeLibrary/api/attributeQueries';
import {
  composeDraftBlurHandler,
  scheduleDraftPersistOnBlur,
} from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX } from './TextFieldComponent';
const handleMultiSelectOptionEqualToValue = (option, val) =>
  option?.value === val?.value;

const handleMultiselectGetOptionLabel = (option) => option?.label;

/**
 * MultiSelect field component for selecting multiple items from a list
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {Array} props.value - Current field values
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance for error handling
 * @returns {React.ReactElement} The MultiSelect component
 */
const MultiSelectFieldComponent = ({
  label,
  titleLabel,
  value = EMPTY_ARRAY,
  onChange,
  attribute,
  entityDetailsFormikForm,
  overrideDropdownOptions = null,
  isLoadingConstraints = false,
  configColumn = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const { execute: fetchSingleAttributeDetails } = useSingleAttributeQuery({
    options: { enabled: false, loader: false },
  });
  const [fetchedOptions, setFetchedOptions] = useState(null);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isConstraintMultiSelect =
    Array.isArray(overrideDropdownOptions) &&
    overrideDropdownOptions.length > 0;

  const multiSelectOptions = useMemo(() => {
    let optionsToUse = [];
    if (
      overrideDropdownOptions !== null &&
      overrideDropdownOptions !== undefined
    ) {
      optionsToUse = overrideDropdownOptions;
    } else if (!isValueEmpty(attribute?.drop_down_options)) {
      optionsToUse = attribute?.drop_down_options;
    } else if (fetchedOptions !== null) {
      optionsToUse = fetchedOptions;
    }

    // Ensure optionsToUse is always an array before calling .map()
    optionsToUse = Array.isArray(optionsToUse) ? optionsToUse : [];

    const mapped =
      optionsToUse?.length > 0
        ? optionsToUse?.map((entity) => {
            // Handle both string values and option objects
            if (typeof entity === 'object') {
              return {
                label: entity?.label || entity?.value,
                value: entity?.value,
              };
            }
            return { label: entity, value: entity };
          })
        : [];

    // Deduplicate options by value to prevent filtering issues
    const seen = new Set();
    return mapped?.filter((opt) => {
      if (seen?.has(opt?.value)) {
        return false;
      }
      seen?.add(opt?.value);
      return true;
    });
  }, [fetchedOptions, overrideDropdownOptions, attribute?.drop_down_options]);

  const multiselectValue = useMemo(() => {
    if (isEmpty(value) || !Array.isArray(value)) return [];
    if (!isConstraintMultiSelect) return value;

    const selectedValues = value
      .map((item) => (typeof item === 'object' ? item?.value : item))
      .filter(Boolean);

    return multiSelectOptions.filter((option) =>
      selectedValues.includes(option?.value)
    );
  }, [isConstraintMultiSelect, multiSelectOptions, value]);

  const handleInputBlur = useCallback(
    (event) => {
      composeDraftBlurHandler(
        entityDetailsFormikForm?.handleBlur,
        onDraftBlur
      )(event);
    },
    [entityDetailsFormikForm?.handleBlur, onDraftBlur]
  );

  const handleRenderMultiSelectInput = useCallback(
    (params) => (
      <TextField
        {...params}
        name={attribute?.id}
        label={label}
        title={titleLabel}
        placeholder={multiselectValue?.length > 0 ? '' : 'Select value'}
        spellCheck={true}
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors[attribute?.id],
            entityDetailsFormikForm?.touched[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors[attribute?.id],
          entityDetailsFormikForm?.touched[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
      />
    ),
    [
      label,
      titleLabel,
      entityDetailsFormikForm,
      attribute?.id,
      hasDraftChip,
      multiselectValue,
    ]
  );

  const handleChangeMultiSelect = useCallback(
    (newValue) => {
      if (isValueEmpty(newValue)) {
        onChange(null);
      } else {
        onChange(newValue);
      }
      // Chip X removal doesn't fire a blur event, so we schedule draft manually.
      // For dropdown selections, handleInputBlur will handle it on blur.
      if (!isDropdownOpen) {
        scheduleDraftPersistOnBlur(onDraftBlur);
      }
    },
    [onChange, onDraftBlur, isDropdownOpen]
  );

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const handleOpen = useCallback(async () => {
    setIsDropdownOpen(true);
    // Only fetch if we have no options and haven't fetched yet
    if (
      multiSelectOptions.length === 0 &&
      attribute?.id &&
      fetchedOptions === null &&
      configColumn
    ) {
      setIsFetchingOptions(true);
      try {
        const data = await fetchSingleAttributeDetails({
          params: { attribute_id: attribute?.id },
        });
        const enrichedOptions = data?.drop_down_options || [];
        setFetchedOptions(enrichedOptions);
      } catch (_err) {
        setFetchedOptions([]);
      } finally {
        setIsFetchingOptions(false);
      }
    }
  }, [
    multiSelectOptions.length,
    attribute?.id,
    fetchedOptions,
    configColumn,
    fetchSingleAttributeDetails,
  ]);

  return (
    <Multiselect
      options={multiSelectOptions}
      value={multiselectValue}
      getOptionLabel={handleMultiselectGetOptionLabel}
      isOptionEqualToValue={handleMultiSelectOptionEqualToValue}
      renderInput={handleRenderMultiSelectInput}
      onChange={handleChangeMultiSelect}
      onBlur={handleInputBlur}
      onOpen={handleOpen}
      onClose={handleDropdownClose}
      loading={isLoadingConstraints || isFetchingOptions}
      readOnly={isLoadingConstraints}
      loadingText="Loading options..."
    />
  );
};

MultiSelectFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object])
  ),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    drop_down_options: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    handleBlur: PropTypes.func,
  }),
  overrideDropdownOptions: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
  isLoadingConstraints: PropTypes.bool,
  configColumn: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default MultiSelectFieldComponent;
